import React from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  TestsFull_tests_pagination,
  AllEventAttendees_allEventAttendees_attendees,
  getFormattedDateAndTime,
  getResultLabelColor,
  SortByType,
  formatDOB,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { DownloadTestResultPdfEventButton } from 'sharedComponents/DownloadTestResultPdfButton'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Label from 'components/Label'
import Card from 'components/Card'
import { isInfluenza } from 'utils/helpers'

interface ColumnLabels {
  groupCompany?: string | null
  patient?: string | null
  staff?: string | null
  result?: string | null
  resultReceivedAt?: string | null
  testedAt?: string | null
  resultProvisionedAt?: string | null
  location?: string | null
  city?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  data: AllEventAttendees_allEventAttendees_attendees[]
  pagination?: TestsFull_tests_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
  onRowClick?: (data: AllEventAttendees_allEventAttendees_attendees) => void
}

const defaultColumnLabels = {
  event: 'Event',
  patient: 'Patient',
  staff: 'Staff',
  result: 'Result',
  testedAt: 'Specimen Collected At',
  resultProvisionedAt: 'Released at',
  city: 'City',
  state: 'State',
}

const PAGE_LENGTH = 10

export const EventSamplesDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  data,
  sorter,
  pagination,
  isLoading,
  columnLabels: providedColumnLabels,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={data}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.event) ? (
                <Datalist.HeaderCell label={columnLabels.event} />
              ) : null}
              {!isNull(columnLabels.patient) ? (
                <Datalist.HeaderCell label={columnLabels.patient} />
              ) : null}
              {!isNull(columnLabels.city) ? (
                <Datalist.HeaderCell label={columnLabels.city} />
              ) : null}
              {!isNull(columnLabels.state) ? (
                <Datalist.HeaderCell label={columnLabels.state} />
              ) : null}
              {!isNull(columnLabels.staff) ? (
                <Datalist.HeaderCell label={columnLabels.staff} />
              ) : null}
              {!isNull(columnLabels.result) ? (
                <Datalist.HeaderCell sortKey={SortByType.RESULT} label={columnLabels.result} />
              ) : null}
              {!isNull(columnLabels.testedAt) ? (
                <Datalist.HeaderCell sortKey={SortByType.TESTED_AT} label={columnLabels.testedAt} />
              ) : null}
              {!isNull(columnLabels.resultProvisionedAt) ? (
                <Datalist.HeaderCell
                  sortKey={SortByType.RESULT_PROVISIONED_AT}
                  label={columnLabels.resultProvisionedAt}
                />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const { Event, EventSamples, firstName, lastName, dateOfBirth, city, state } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(data)
                      }
                    : undefined
                }
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.groupCompany) ? (
                  <Datalist.Cell label={columnLabels.groupCompany}>
                    {Event.name || ''}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.patient) ? (
                  <Datalist.Cell label={columnLabels.patient}>
                    {firstName ?? ''} {lastName ?? ''} ({formatDOB(dateOfBirth ?? '')})
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.city) ? (
                  <Datalist.Cell label={columnLabels.city}>{city}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.state) ? (
                  <Datalist.Cell label={columnLabels.state}>{state}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.staff) ? (
                  <Datalist.Cell label={columnLabels.staff}>
                    {EventSamples?.[0]?.TestedBy && (
                      <>
                        {EventSamples?.[0]?.TestedBy?.firstName ?? ''}{' '}
                        {EventSamples?.[0]?.TestedBy?.lastName ?? ''}
                      </>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.result) ? (
                  <Datalist.Cell label={columnLabels.result}>
                    {EventSamples?.[0] ? (
                      <>
                        <Label
                          indicator={
                            EventSamples?.[0]?.result
                              ? getResultLabelColor(EventSamples?.[0].result)
                              : 'blue'
                          }
                        >
                          {isInfluenza(Event.testType) && 'A: '}
                          {EventSamples?.[0]?.result || 'Pending result'}
                        </Label>
                        {isInfluenza(Event.testType) && EventSamples?.[0]?.secondResult && (
                          <Label indicator={getResultLabelColor(EventSamples?.[0]?.secondResult)}>
                            {`B: ${EventSamples?.[0]?.secondResult}`}
                          </Label>
                        )}
                        {EventSamples?.[0]?.result && (
                          <DownloadTestResultPdfEventButton
                            appearance="link"
                            size="small"
                            testId={EventSamples?.[0].id}
                            testType={Event.testType || ''}
                            isDisabled={!EventSamples?.[0].result}
                          />
                        )}
                      </>
                    ) : (
                      <Label indicator="red">Not tested yet</Label>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.testedAt) ? (
                  <Datalist.Cell label={columnLabels.testedAt}>
                    {getFormattedDateAndTime(EventSamples?.[0]?.testedAt, true)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.resultProvisionedAt) ? (
                  <Datalist.Cell label={columnLabels.resultProvisionedAt}>
                    {!EventSamples?.[0]?.result ? (
                      'Pending result'
                    ) : (
                      <>
                        {EventSamples?.[0]?.resultProvisionedAt ? (
                          getFormattedDateAndTime(EventSamples?.[0]?.resultProvisionedAt, true)
                        ) : (
                          <Label indicator="red">Not released yet</Label>
                        )}
                      </>
                    )}
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
    </>
  )
}
